import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  formControl: {
    width: "100%"
  }
}));

const toInputDateTime = value => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

const TaskModal = ({ open, onClose, task, users, currentUser }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    dueDate: "",
    assignedToUserId: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = Boolean(task?.id);

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "pt-BR")
      ),
    [users]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setFormData({
      title: task?.title || "",
      description: task?.description || "",
      dueDate: toInputDateTime(task?.dueDate),
      assignedToUserId: task?.assignedToUserId || currentUser?.id || ""
    });
  }, [task, open, currentUser]);

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.dueDate || !formData.assignedToUserId) {
      toast.error("Preencha titulo, prazo e responsavel.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        assignedToUserId: Number(formData.assignedToUserId)
      };

      if (isEditing) {
        await api.put(`/tasks/${task.id}`, payload);
      } else {
        await api.post("/tasks", payload);
      }

      toast.success(isEditing ? "Tarefa atualizada." : "Tarefa criada.");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <DialogTitle>
        {isEditing ? "Editar tarefa" : "Nova tarefa"}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Titulo"
              name="title"
              value={formData.title}
              onChange={handleChange}
              variant="outlined"
              margin="dense"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descricao"
              name="description"
              value={formData.description}
              onChange={handleChange}
              variant="outlined"
              margin="dense"
              fullWidth
              multiline
              rows={4}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Prazo"
              name="dueDate"
              type="datetime-local"
              value={formData.dueDate}
              onChange={handleChange}
              variant="outlined"
              margin="dense"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl
              variant="outlined"
              margin="dense"
              className={classes.formControl}
            >
              <InputLabel id="task-user-select-label">Responsavel</InputLabel>
              <Select
                labelId="task-user-select-label"
                name="assignedToUserId"
                value={formData.assignedToUserId}
                onChange={handleChange}
                label="Responsavel"
              >
                {sortedUsers.map(user => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={isSubmitting}
          className={classes.btnWrapper}
        >
          {isEditing ? "Salvar" : "Criar"}
          {isSubmitting && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskModal;
